<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Wavy top banner -->
      <div class="wave-header">
        <div class="wh-inner">
          <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="wh-logo" />
          
        </div>
        <svg class="wave-svg" viewBox="0 0 1440 48" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,32 C240,0 480,64 720,32 C960,0 1200,64 1440,32 L1440,48 L0,48 Z" fill="#faf6f0"/>
        </svg>
      </div>

      <!-- Hero -->
      <section class="cot-hero">
        <div class="ch-deco ch-deco-left" aria-hidden="true">
          <div class="cd-dot"></div>
          <div class="cd-dot cd-dot2"></div>
          <div class="cd-dot cd-dot3"></div>
        </div>
        <div class="ch-content">
          <div class="ch-tag">
            <span class="ch-leaf">🏡</span>
            <span>Find Your Home</span>
          </div>
          <h1 class="cot-h1" v-html="heroH1Html"></h1>
          <p class="cot-body">{{ heroBody }}</p>
          <div class="ch-btns">
            <a class="ch-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="ch-btn-soft" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
          <div class="ch-chips">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="ch-chip">📞 {{ loinfo.phone_number }}</a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="ch-chip">✉ Email</a>
          </div>
        </div>
        <div class="ch-deco ch-deco-right" aria-hidden="true">
          <div class="cd-ring cr1"></div>
          <div class="cd-ring cr2"></div>
        </div>
      </section>

      <!-- Cards in a cozy band -->
      <section class="cot-cards-band">
        <div class="ccb-inner">

          <div class="cot-card lo-cot">
            <div class="cc-top-bar lo-top-bar"></div>
            <div class="cc-body">
              <div class="cc-photo-wrap">
                <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="cc-photo" />
              </div>
              <div class="cc-badge lo-badge">Loan Officer</div>
              <div class="cc-name">{{ loName }}</div>
              <div class="cc-role">{{ loTitle }}</div>
              <div v-if="loinfo && loinfo.nmls" class="cc-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              <div class="cc-sep lo-sep"></div>
              <div class="cc-contacts">
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="cc-contact lo-contact">{{ loinfo.phone_number }}</a>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="cc-contact lo-contact">{{ loinfo.email }}</a>
              </div>
          
            </div>
          </div>

          <div class="cot-card re-cot">
            <div class="cc-top-bar re-top-bar"></div>
            <div class="cc-body">
              <div class="cc-photo-wrap">
                <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="cc-photo" />
              </div>
              <div class="cc-badge re-badge">Partner</div>
              <div class="cc-name">{{ realtorName }}</div>
              <div class="cc-role">{{ realtor && realtor.brokerage_name }}</div>
              <div class="cc-sep re-sep"></div>
              <div class="cc-contacts">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="cc-contact re-contact">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="cc-contact re-contact">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="cc-contact re-contact" target="_blank" rel="noreferrer">Visit Site →</a>
              </div>
            </div>
          </div>

        </div>
      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandCottage",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Welcome home,<br/><em>truly.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;0,600;1,400&family=Nunito:wght@300;400;500;600;700&display=swap');

.page {
  min-height: 100vh;
  background: #faf6f0;
  color: #1e1810;
  font-family: 'Nunito', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --warm: #d4956a;
  --bg: #faf6f0;
  --card-bg: #ffffff;
  --border: rgba(30,24,16,0.1);
  --muted: #7a6e60;
  overflow: hidden;
}
.root { display: flex; flex-direction: column; min-height: 100vh; }

/* WAVE HEADER */
.wave-header { background: var(--p); position: relative; }
.wh-inner { display: flex; align-items: center; justify-content: space-between; padding: 18px 56px; }
.wh-logo { width: 140px; max-height:72px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.85; }
.wh-cta { font-size: 12px; font-weight: 700; text-decoration: none; background: var(--a); color: var(--p); padding: 10px 22px; border-radius: 24px; transition: opacity 0.2s; }
.wh-cta:hover { opacity: 0.88; }
.wave-svg { display: block; width: 100%; height: 48px; margin-bottom: -1px; }

/* HERO */
.cot-hero { display: flex; align-items: center; justify-content: center; padding: 52px 40px 48px; position: relative; overflow: hidden; }
.ch-deco { position: absolute; pointer-events: none; }
.ch-deco-left { left: 40px; top: 50%; transform: translateY(-50%); }
.ch-deco-right { right: 40px; top: 50%; transform: translateY(-50%); }
.cd-dot { width: 12px; height: 12px; border-radius: 50%; background: var(--a); opacity: 0.25; margin-bottom: 12px; }
.cd-dot2 { width: 8px; height: 8px; opacity: 0.15; }
.cd-dot3 { width: 16px; height: 16px; opacity: 0.1; }
.cd-ring { border-radius: 50%; border: 2px solid; }
.cr1 { width: 80px; height: 80px; border-color: color-mix(in srgb, var(--a) 18%, transparent); }
.cr2 { width: 50px; height: 50px; border-color: color-mix(in srgb, var(--p) 10%, transparent); margin: 16px auto 0; }
.ch-content { text-align: center; max-width: 600px; }
.ch-tag { display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 12px; color: var(--muted); margin-bottom: 18px; }
.ch-leaf { font-size: 14px; }
.cot-h1 { font-family: 'Spectral', serif; font-size: clamp(38px, 5vw, 62px); font-weight: 600; line-height: 1.1; color: var(--p); margin: 0 0 18px; }
.cot-h1 em { font-style: italic; color: color-mix(in srgb, var(--warm) 80%, var(--a)); }
.cot-body { font-size: 15px; line-height: 1.8; color: var(--muted); margin-bottom: 28px; font-weight: 300; }
.ch-btns { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; margin-bottom: 20px;justify-content: center; }
.ch-btn { width:55%;font-size: 13px; font-weight: 700; text-decoration: none; background: var(--p); color: #fff; padding: 13px 30px; border-radius: 24px; transition: opacity 0.2s; }
.ch-btn:hover { opacity: 0.88; }
.ch-btn-soft { font-size: 13px; font-weight: 400; text-decoration: none; color: var(--muted); border: 1.5px solid var(--border); padding: 12px 24px; border-radius: 24px; transition: background 0.18s; }
.ch-btn-soft:hover { background: rgba(255,255,255,0.8); }
.ch-chips { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; }
.ch-chip { font-size: 12px; text-decoration: none; color: var(--muted); background: white; border: 1px solid var(--border); padding: 7px 16px; border-radius: 20px; transition: border-color 0.18s; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
.ch-chip:hover { border-color: var(--a); color: var(--p); }

/* CARDS BAND */
.cot-cards-band { background: #f0ebe4; padding: 52px 40px; border-top: 1px solid var(--border); }
.ccb-inner { max-width: 840px; margin: 0 auto; display: flex; gap: 20px; flex-wrap: wrap; justify-content: center; }
.cot-card { flex: 0 0 360px; background: var(--card-bg); border-radius: 20px; overflow: hidden; box-shadow: 0 4px 20px rgba(30,24,16,0.07); border: 1px solid var(--border); transition: transform 0.2s; }
.cot-card:hover { transform: translateY(-4px); }
.cc-top-bar { height: 8px; border-radius: 20px 20px 0 0; }
.lo-top-bar { background: linear-gradient(90deg, var(--p), color-mix(in srgb, var(--p) 50%, var(--a))); }
.re-top-bar { background: linear-gradient(90deg, var(--a), color-mix(in srgb, var(--a) 60%, var(--warm))); }
.cc-body { padding: 24px; display: flex; flex-direction: column; align-items: center; text-align: center; }
.cc-photo-wrap { margin-bottom: 12px; }
.cc-photo { width: 72px; height: 72px; border-radius: 50%; object-fit: cover; border: 4px solid white; box-shadow: 0 3px 12px rgba(0,0,0,0.12); }
.cc-badge { font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; padding: 4px 12px; border-radius: 20px; margin-bottom: 8px; }
.lo-badge { color: var(--p); background: color-mix(in srgb, var(--p) 8%, transparent); border: 1px solid color-mix(in srgb, var(--p) 14%, transparent); }
.re-badge { color: color-mix(in srgb, var(--a) 80%, #1e1810); background: color-mix(in srgb, var(--a) 10%, transparent); border: 1px solid color-mix(in srgb, var(--a) 18%, transparent); }
.cc-name { font-family: 'Spectral', serif; font-size: 20px; font-weight: 600; color: var(--p); margin-bottom: 2px; }
.cc-role { font-size: 12px; color: var(--muted); }
.cc-nmls { font-size: 10px; color: color-mix(in srgb, var(--muted) 60%, transparent); margin-top: 2px; }
.cc-sep { width: 40px; height: 2px; margin: 14px auto; border-radius: 2px; }
.lo-sep { background: var(--p); }
.re-sep { background: var(--a); }
.cc-contacts { display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; width: 100%; }
.cc-contact { font-size: 11px; text-decoration: none; transition: color 0.18s; color: var(--muted); }
.lo-contact:hover { color: var(--p); }
.re-contact:hover { color: color-mix(in srgb, var(--a) 80%, #1e1810); }
.cc-apply { display: block; width: 100%; box-sizing: border-box; text-align: center; text-decoration: none; font-size: 13px; font-weight: 700; background: var(--p); color: #fff; padding: 12px; border-radius: 12px; transition: opacity 0.2s; }
.cc-apply:hover { opacity: 0.88; }

@media (max-width: 820px) {
  .wh-inner { padding: 14px 24px; }
  .cot-hero { padding: 40px 24px 32px; }
  .ch-deco { display: none; }
  .cot-cards-band { padding: 36px 24px; }
  .cot-card { flex: 0 0 100%; }
}
</style>
